import { useState } from "react";
import { Sparkles, Send, Clock, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClarityCall = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    challenge: "",
    commitment: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCommitmentChange = (value) => {
    setFormData((prev) => ({ ...prev, commitment: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/clarity-call`, formData);
      toast.success("Application submitted! We'll be in touch shortly.");
      setSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        challenge: "",
        commitment: "",
      });
    } catch (e) {
      toast.error("Failed to submit application. Please try again.");
    }
    setIsSubmitting(false);
  };

  return (
    <section
      id="clarity-call"
      data-testid="clarity-call-section"
      className="py-20 md:py-32 bg-gradient-to-br from-primary/5 via-accent/5 to-background"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Poster/Marketing Content */}
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary uppercase tracking-wide">
                Complimentary Offer
              </span>
            </div>

            <div>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
                Complimentary <span className="text-primary">45-Minute</span> Private Consultation
              </h2>
              <p className="text-xl text-muted-foreground font-medium">
                Career/Coaching Clarity Call
              </p>
            </div>

            <div className="p-6 bg-background rounded-2xl border-2 border-primary/20 shadow-lg">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-2 bg-accent/20 rounded-lg">
                  <Sparkles className="h-5 w-5 text-accent-foreground" />
                </div>
                <span className="text-sm font-semibold uppercase tracking-wide text-primary">
                  Application Required
                </span>
              </div>
              <p className="text-base text-foreground/80 leading-relaxed">
                This exclusive consultation is designed for professionals who are ready to invest 
                in their growth. Complete the application to secure your spot.
              </p>
            </div>

            {/* Benefits */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg mt-1">
                  <Clock className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold">45 Minutes of Focused Clarity</h4>
                  <p className="text-sm text-muted-foreground">
                    Deep, personalized conversation to explore your challenges and goals
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg mt-1">
                  <Users className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold">Private 1:1 Session</h4>
                  <p className="text-sm text-muted-foreground">
                    Confidential space to discuss your career and leadership journey
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg mt-1">
                  <Sparkles className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold">Actionable Insights</h4>
                  <p className="text-sm text-muted-foreground">
                    Walk away with clarity, direction, and next steps
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Application Form */}
          <Card className="border-2 border-primary/20 shadow-xl">
            <CardHeader className="bg-primary/5 border-b border-primary/10">
              <CardTitle className="text-2xl">Application Form</CardTitle>
              <CardDescription>
                Please fill in all the details below. All fields are required.
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Application Submitted!</h3>
                  <p className="text-muted-foreground mb-6">
                    Thank you for applying. Deepa will review your application and get back to you within 24-48 hours.
                  </p>
                  <Button
                    data-testid="clarity-another-btn"
                    onClick={() => setSubmitted(false)}
                    variant="outline"
                  >
                    Submit Another Application
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="clarity-name">Full Name *</Label>
                    <Input
                      id="clarity-name"
                      name="name"
                      data-testid="clarity-name-input"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="clarity-email">Email Address *</Label>
                    <Input
                      id="clarity-email"
                      name="email"
                      type="email"
                      data-testid="clarity-email-input"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="clarity-phone">Phone Number (for WhatsApp Call) *</Label>
                    <Input
                      id="clarity-phone"
                      name="phone"
                      type="tel"
                      data-testid="clarity-phone-input"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+61 400 000 000"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="clarity-challenge">
                      What type of challenge are you currently facing? *
                    </Label>
                    <Textarea
                      id="clarity-challenge"
                      name="challenge"
                      data-testid="clarity-challenge-input"
                      value={formData.challenge}
                      onChange={handleInputChange}
                      placeholder="Share the challenges you're facing in your career or leadership journey..."
                      rows={4}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>
                      Are you ready to commit to your personal development and growth? *
                    </Label>
                    <Select onValueChange={handleCommitmentChange} value={formData.commitment} required>
                      <SelectTrigger data-testid="clarity-commitment-select">
                        <SelectValue placeholder="Select your commitment level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes-ready-action">
                          Yes, I am ready to take action
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    type="submit"
                    data-testid="clarity-submit-btn"
                    disabled={
                      isSubmitting ||
                      !formData.name ||
                      !formData.email ||
                      !formData.phone ||
                      !formData.challenge ||
                      !formData.commitment
                    }
                    className="w-full bg-primary text-white hover:bg-primary/90"
                    size="lg"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="spinner w-4 h-4" />
                        Submitting...
                      </span>
                    ) : (
                      <>
                        Submit Application
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ClarityCall;
